import React from 'react'
import Paper from '@mui/material/Paper'
import { Link, useLoaderData } from 'react-router-dom'
import { formatDateYYYYMMDDhhmmss } from '../../../../packages/utils/numberFormatters'
import Grid from '@mui/material/Grid'
import { PressReleaseEntity } from '@finance-lab/utils/types'
import { CompanyPressReleases } from './CompanyPressReleases'
import { Helmet } from '@dr.pogodin/react-helmet'
import { ClockIcon, FileIcon } from 'lucide-react'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListSubheader from '@mui/material/ListSubheader'
import ListItemText from '@mui/material/ListItemText'
import { PressReleaseRelatedTransactions } from '../../../../components/entities/PressReleaseRelatedTransactions'
import { getPressReleaseLink } from '../../../../utils/get-press-release-link'
import { flagPath } from '../../../../components/entities/CompaniesGrid'

const PaperWrapper = ({ children }: { children: React.ReactNode }) => {
    return <Paper sx={{ padding: 4, maxWidth: 800, img: { maxWidth: '100%' } }}>{children}</Paper>
}

export const PressRelease = () => {
    const pressRelease = useLoaderData() as PressReleaseEntity

    const bodyContent = pressRelease.body
    const publishDate = pressRelease.publishedAt
    const attachments = pressRelease.attachments.map((a) => ({
        ...a,
        caption: a.caption ?? a.url.split('/').pop(),
    }))

    return (
        <>
            <Helmet title={pressRelease.title} />
            <Grid container spacing={4}>
                <Grid size={{ md: 8 }}>
                    <PaperWrapper>
                        <ul style={{ margin: 0, padding: 0, textAlign: 'right' }}>
                            {pressRelease.translations.map((t) => (
                                <Link key={t.prId} to={getPressReleaseLink(pressRelease.companyId, t.prId)}>
                                    <img
                                        src={flagPath(
                                            t.language === 'en' ? 'gb' : t.language == 'sv' ? 'se' : t.language,
                                        )}
                                        height={18}
                                        style={{ marginLeft: 14 }}
                                    />
                                </Link>
                            ))}
                        </ul>
                        <h1>{pressRelease.title}</h1>

                        <ul>
                            {pressRelease.translations.map((t) => (
                                <li key={t.prId}>
                                    <Link to={getPressReleaseLink(pressRelease.companyId, t.prId)}>
                                        {t.language}: {Math.round(t.accuracyScore * 100)} %
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <Box
                            sx={{
                                display: 'inline-flex',
                                flexDirection: 'row',
                                gap: '4px',
                                alignItems: 'center',
                            }}
                        >
                            <ClockIcon size={16} />
                            {formatDateYYYYMMDDhhmmss(publishDate)}
                        </Box>

                        <Box sx={{ marginTop: 2 }}>
                            <PressReleaseRelatedTransactions relatedTransactions={pressRelease.relatedTransactions} />
                        </Box>

                        <Box sx={{ marginTop: 4 }} dangerouslySetInnerHTML={{ __html: bodyContent }} />

                        {attachments.length > 0 && (
                            <div>
                                <List subheader={<ListSubheader>Länkade filer</ListSubheader>}>
                                    {attachments.map(({ caption, url }) => (
                                        <ListItemButton key={url} component='a' href={url} target='_blank'>
                                            <ListItemIcon>
                                                <FileIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={caption || url} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </div>
                        )}
                    </PaperWrapper>
                </Grid>
                <Grid size={{ md: 4 }}>
                    <CompanyPressReleases companyId={pressRelease.companyId} currentPrId={pressRelease.id} />
                </Grid>
            </Grid>
        </>
    )
}
